import { useCallback, useState } from 'react'

/** Inline text field — always-visible input (Framer-style, commit on blur/Enter) */
export function InlineTextField({
  label,
  value,
  onChange,
  placeholder,
  show,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  placeholder?: string
  show: boolean
}) {
  const [draft, setDraft] = useState(value)
  const [focused, setFocused] = useState(false)

  // Sync draft when value changes externally (and not currently editing)
  if (!focused && draft !== value) {
    setDraft(value)
  }

  const handleBlur = () => {
    setFocused(false)
    if (draft !== value) {
      onChange(draft)
    }
  }

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault()
          ; (e.target as HTMLElement).blur()
      }
      if (e.key === 'Escape') {
        setDraft(value)
          ; (e.target as HTMLElement).blur()
      }
    },
    [value],
  )

  if (!show) return null

  return (
    <div className="flex justify-between items-center gap-2">
      {label && <span className="text-muted-foreground text-xs shrink-0">{label}</span>}
      <input
        data-testid={`text-${label.toLowerCase().replace(/\s+/g, '-')}`}
        className="flex-1 min-w-0 max-w-[160px] bg-transparent border border-transparent rounded px-1.5 py-0.5 text-xs text-right hover:border-input focus:border-input focus:bg-muted/70 focus:outline-none focus:ring-1 focus:ring-ring transition-colors"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
      />
    </div>
  )
}
